import { RISK_LEVEL, SANCTIONED_STATUS, SHADOW_AI_LOOKBACK_DAYS, TONE } from './constants'

const MIDDAY_HOUR = 12

export const copilotConfigurationRows = [
  {
    key: 'semantic-index',
    setting: 'Semantic index rollout scope',
    current: 'Tenant default',
    recommended: 'Pilot group first',
  },
  { key: 'rss', setting: 'Restricted SharePoint Search', current: 'Not assessed', recommended: 'Enable for pilot' },
  { key: 'audit', setting: 'Copilot audit retention', current: 'Standard', recommended: 'Extend retention' },
]

export const deviceRows = [
  {
    key: 'device-1',
    device: 'SURFACE-1824',
    user: 'Avery Taylor',
    os: 'Windows 10',
    issue: 'Non-compliant',
    tone: TONE.DANGER,
  },
  { key: 'device-2', device: 'MACBOOK-74', user: 'Morgan Lee', os: 'macOS', issue: 'Personal device', tone: TONE.WARN },
  {
    key: 'device-3',
    device: 'WIN11-552',
    user: 'Jordan Smith',
    os: 'Windows 11',
    issue: 'Encryption unknown',
    tone: TONE.WARN,
  },
]

// Deterministic 30-day activity used only by the license-required subscription preview.
const buildDailyActivity = ({ peakUsers = 0, density = 0.5 }) => {
  const activity = []
  for (let offset = SHADOW_AI_LOOKBACK_DAYS - 1; offset >= 0; offset -= 1) {
    const date = new Date()
    date.setHours(MIDDAY_HOUR, 0, 0, 0)
    date.setDate(date.getDate() - offset)
    const dayOfWeek = date.getDay()
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6
    const wave = (date.getDate() * 3 + dayOfWeek * 2) % 10
    const isActive = !isWeekend && wave < density * 10
    const users = isActive ? Math.max(1, Math.round(peakUsers * (0.3 + (wave % 4) * 0.2))) : 0
    activity.push({ date: date.toISOString().slice(0, 10), users })
  }
  return activity
}

// Illustrative Discover-backed AI apps for the license-required subscription preview.
export const shadowAiAppRows = [
  {
    entityId: 'chatgpt',
    name: 'ChatGPT',
    url: 'chat.openai.com',
    users: 42,
    sanctioned: SANCTIONED_STATUS.UNSANCTIONED,
    risk: RISK_LEVEL.HIGH,
    ssoSupport: true,
    mfaSupport: true,
    latestBreach: '2023-03-20',
    breachUrl: 'https://haveibeenpwned.com/breach/OpenAI',
    dailyActivity: buildDailyActivity({ peakUsers: 42, density: 0.85 }),
  },
  {
    entityId: 'perplexity',
    name: 'Perplexity',
    url: 'perplexity.ai',
    users: 13,
    sanctioned: SANCTIONED_STATUS.UNSANCTIONED,
    risk: RISK_LEVEL.MEDIUM,
    ssoSupport: false,
    mfaSupport: true,
    latestBreach: null,
    breachUrl: null,
    dailyActivity: buildDailyActivity({ peakUsers: 13, density: 0.5 }),
  },
  {
    entityId: 'gemini',
    name: 'Google Gemini',
    url: 'gemini.google.com',
    users: 8,
    sanctioned: SANCTIONED_STATUS.PENDING,
    risk: RISK_LEVEL.MEDIUM,
    ssoSupport: true,
    mfaSupport: true,
    latestBreach: null,
    breachUrl: null,
    dailyActivity: buildDailyActivity({ peakUsers: 8, density: 0.35 }),
  },
  {
    entityId: 'claude',
    name: 'Claude',
    url: 'claude.ai',
    users: 25,
    sanctioned: SANCTIONED_STATUS.SANCTIONED,
    risk: RISK_LEVEL.LOW,
    ssoSupport: true,
    mfaSupport: true,
    latestBreach: null,
    breachUrl: null,
    dailyActivity: buildDailyActivity({ peakUsers: 25, density: 0.7 }),
  },
  {
    entityId: 'character-ai',
    name: 'Character.AI',
    url: 'character.ai',
    users: 3,
    sanctioned: SANCTIONED_STATUS.UNKNOWN,
    risk: RISK_LEVEL.UNKNOWN,
    ssoSupport: false,
    mfaSupport: false,
    latestBreach: '2021-08-14',
    breachUrl: 'https://haveibeenpwned.com/breach/CharacterAI',
    dailyActivity: buildDailyActivity({ peakUsers: 3, density: 0.2 }),
  },
]

// Illustrative users for the license-required subscription preview.
const mockUnsanctionedApp = (entityId) => {
  const {
    name = null,
    url = null,
    sanctioned = null,
    risk = null,
  } = shadowAiAppRows.find((app) => app.entityId === entityId) ?? {}
  return { name, url, sanctioned, risk, lastSeen: null }
}

export const shadowAiUserRows = {
  total: 7,
  discoverUsersTotal: 24,
  items: [
    {
      entityId: 'j.rivera@contoso.com',
      name: 'Jordan Rivera',
      user: 'j.rivera@contoso.com',
      apps: ['chatgpt', 'perplexity', 'character-ai'].map(mockUnsanctionedApp),
      lastSeen: '2h ago',
    },
    {
      entityId: 'a.chen@contoso.com',
      name: 'Alex Chen',
      user: 'a.chen@contoso.com',
      apps: ['chatgpt', 'gemini'].map(mockUnsanctionedApp),
      lastSeen: '5h ago',
    },
    {
      entityId: 'm.okafor@contoso.com',
      name: 'Michael Okafor',
      user: 'm.okafor@contoso.com',
      apps: ['perplexity', 'chatgpt'].map(mockUnsanctionedApp),
      lastSeen: '1d ago',
    },
    {
      entityId: 's.patel@contoso.com',
      name: 'Sana Patel',
      user: 's.patel@contoso.com',
      apps: ['chatgpt'].map(mockUnsanctionedApp),
      lastSeen: '2d ago',
    },
    {
      entityId: 'l.nguyen@contoso.com',
      name: 'Linh Nguyen',
      user: 'l.nguyen@contoso.com',
      apps: ['gemini', 'perplexity'].map(mockUnsanctionedApp),
      lastSeen: '3d ago',
    },
    {
      entityId: 'r.dubois@contoso.com',
      name: 'Renée Dubois',
      user: 'r.dubois@contoso.com',
      apps: ['character-ai'].map(mockUnsanctionedApp),
      lastSeen: '6d ago',
    },
    {
      entityId: 'k.silva@contoso.com',
      name: 'Kai Silva',
      user: 'k.silva@contoso.com',
      apps: ['chatgpt', 'perplexity'].map(mockUnsanctionedApp),
      lastSeen: '9d ago',
    },
  ].map((row) => ({ ...row, appCount: row.apps.length })),
}
